import React from 'react';
import { theme } from './theme';

/* AppBar — top brand / status strip used by every v2 dashboard.
 * Sticky, white, with a soft border-bottom and a live status pill on the right.
 * Includes auto-refresh controls: pause/resume + manual refresh + last-updated time. */

type Props = {
  title: string;
  subtitle?: string;
  loading?: boolean;
  error?: string | null;
  rowCount?: number;
  // Auto-refresh controls
  paused?: boolean;
  onTogglePause?: () => void;
  onRefresh?: () => void;
  lastRefresh?: Date | null;
};

const AppBar: React.FC<Props> = ({
  title, subtitle, loading, error, rowCount,
  paused, onTogglePause, onRefresh, lastRefresh,
}) => {
  const status = error
    ? { dot: theme.accents.red,   text: `Error · ${error}` }
    : loading
      ? { dot: theme.accents.amber, text: 'Loading data…' }
      : paused
        ? { dot: theme.accents.amber, text: 'Paused' }
        : { dot: theme.accents.green, text: `Live · ${(lastRefresh || new Date()).toLocaleTimeString()}` };

  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: '#ffffff',
      borderBottom: '1px solid #eef2f8',
      fontFamily: theme.fontFamily,
    }}>
    <div style={{
      maxWidth: 1760, margin: '0 auto', padding: '14px 40px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 16, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
        {/* brand mark */}
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: `linear-gradient(135deg, ${theme.accents.blue}, ${theme.accents.indigo})`,
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 15, letterSpacing: -0.4,
        }}>HR</div>
        <div style={{ minWidth: 0 }}>
          <h1 style={{
            margin: 0, fontSize: 18, fontWeight: 700,
            color: theme.textPrimary, lineHeight: 1.15, letterSpacing: -0.3,
          }}>
            {title}
          </h1>
          <div style={{
            fontSize: 11, fontWeight: 500, color: theme.textMuted, marginTop: 2,
          }}>
            Halarobo Warehouse Intelligence{subtitle ? ` · ${subtitle}` : ''}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {typeof rowCount === 'number' && (
          <div style={{
            fontSize: 12, fontWeight: 500, color: theme.textMuted,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {rowCount.toLocaleString()} locations loaded
          </div>
        )}

        {/* Status pill */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '6px 12px', borderRadius: 999,
          background: '#fafbfd', border: '1px solid #eef2f8',
          fontSize: 12, fontWeight: 500, color: theme.textSecondary,
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%', background: status.dot,
            animation: (!error && !loading && !paused) ? 'pulse-dot 2s ease-in-out infinite' : 'none',
          }} />
          {status.text}
        </div>

        {/* Auto-refresh controls */}
        {onTogglePause && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: '#fafbfd', border: '1px solid #eef2f8',
            borderRadius: 999, padding: '3px 4px',
          }}>
            {/* Pause / Resume */}
            <button
              onClick={onTogglePause}
              title={paused ? 'Resume auto-refresh (5 min)' : 'Pause auto-refresh'}
              style={{
                width: 30, height: 30, borderRadius: 999,
                border: 'none',
                background: paused ? '#fef3c7' : 'transparent',
                color: paused ? '#d97706' : theme.textSecondary,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14,
                transition: 'all 0.15s ease',
              }}
            >
              {paused ? '▶' : '⏸'}
            </button>
            {/* Manual refresh */}
            <button
              onClick={onRefresh}
              title="Refresh now"
              disabled={loading}
              style={{
                width: 30, height: 30, borderRadius: 999,
                border: 'none',
                background: 'transparent',
                color: loading ? '#cbd5e1' : theme.textSecondary,
                cursor: loading ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14,
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{
                display: 'inline-block',
                animation: loading ? 'spin-refresh 1s linear infinite' : 'none',
              }}>
                ↻
              </span>
            </button>
          </div>
        )}

        {/* Refresh timer hint */}
        {lastRefresh && !paused && !loading && !error && (
          <RefreshCountdown lastRefresh={lastRefresh} />
        )}

        {/* Powered by Halley.AI */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          paddingLeft: 14, marginLeft: 4,
          borderLeft: '1px solid #eef2f8',
          fontSize: 11, fontWeight: 500, color: theme.textMuted,
        }}>
          <span>Powered by</span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontWeight: 700, color: theme.textPrimary, letterSpacing: -0.1,
          }}>
            <span style={{
              width: 16, height: 16, borderRadius: 4,
              background: `linear-gradient(135deg, ${theme.accents.blue}, ${theme.accents.indigo})`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 9, fontWeight: 900,
            }}>H</span>
            Halley.AI
          </span>
        </div>
      </div>
    </div>

    {/* Keyframe animations */}
    <style>{`
      @keyframes pulse-dot {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.4; }
      }
      @keyframes spin-refresh {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
    </header>
  );
};

/* Small helper that shows "Next refresh in Xm Xs" counting down from 5 min. */
const RefreshCountdown: React.FC<{ lastRefresh: Date }> = ({ lastRefresh }) => {
  const [, forceRender] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => forceRender(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = Math.floor((Date.now() - lastRefresh.getTime()) / 1000);
  const remaining = Math.max(0, 300 - elapsed); // 300s = 5 min
  const min = Math.floor(remaining / 60);
  const sec = remaining % 60;

  return (
    <div style={{
      fontSize: 10, fontWeight: 500, color: theme.textMuted,
      fontVariantNumeric: 'tabular-nums', letterSpacing: 0.2, whiteSpace: 'nowrap',
    }}>
      {remaining > 0 ? `${min}:${String(sec).padStart(2, '0')}` : 'Refreshing…'}
    </div>
  );
};

export default AppBar;
